import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { publicOrigin } from "@/lib/publicUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Mail, Loader2, Trash2, Lock, Clock, Check, Copy } from "lucide-react";

const STUDIO_PLUS = ["studio", "enterprise"];

export default function Team() {
  const { user, isOwner } = useAuth();
  const canUseTeam = isOwner || STUDIO_PLUS.includes(user?.tier);

  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: wsList } = await api.get("/workspaces");
      setWorkspaces(wsList);
      const active = wsList.find((w) => w.id === user?.active_workspace_id) || wsList[0];
      setActiveWs(active);
      if (active) {
        const { data: m } = await api.get(`/workspaces/${active.id}/members`);
        setMembers(m);
      }
    } catch { /* ignore */ }
    try {
      const { data: inv } = await api.get("/invites/mine");
      setInvites(inv);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.active_workspace_id]);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!email || !activeWs) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWs.id}/invite`, { email, role });
      toast.success(`Invite created for ${email}`);
      setEmail("");
      // Copy invite URL to clipboard (always mediaothers.com, not preview URL)
      if (data.invite_url) {
        const shareUrl = data.invite_url.startsWith("http")
          ? data.invite_url.replace(/^https?:\/\/[^/]+/, publicOrigin())
          : `${publicOrigin()}${data.invite_url}`;
        try { await navigator.clipboard.writeText(shareUrl); } catch {}
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const remove = async (mid) => {
    if (!activeWs) return;
    if (!window.confirm("Remove this member/invite?")) return;
    try {
      await api.delete(`/workspaces/${activeWs.id}/members/${mid}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const acceptInvite = async (token) => {
    try {
      await api.post(`/invites/${token}/accept`);
      toast.success("Invite accepted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accept failed");
    }
  };

  const copyInviteUrl = async (token) => {
    const url = `${publicOrigin()}/invites/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch { toast.error("Could not copy"); }
  };

  const active = members.filter((m) => m.status === "active");
  const pending = members.filter((m) => m.status === "pending");

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1200px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">STUDIO · TEAM</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Ship together. <span className="font-bold text-amber-400">Faster.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Invite editors and viewers to your workspace. Everyone shares the same credits pool and works on
          the same projects, branding and content calendar.
        </p>
      </div>

      {/* Pending invites addressed to me */}
      {invites.length > 0 && (
        <div className="mb-10 p-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="my-invites">
          <div className="font-mono-tech text-[10px] text-emerald-400 mb-3">YOU · HAVE · {invites.length} · INVITE(S)</div>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 p-3 rounded bg-[#121214] border border-white/5">
                <div className="text-2xl">{inv.workspace_icon || "◆"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold tracking-tight truncate">{inv.workspace_name}</div>
                  <div className="text-xs text-zinc-500">
                    Invited by {inv.inviter_name || inv.inviter_email} · role: <span className="text-amber-400">{inv.role}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => acceptInvite(inv.invite_token)}
                  data-testid={`accept-invite-${inv.id}`}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Accept
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Studio+ gate */}
      {!canUseTeam ? (
        <div className="p-8 rounded-lg border border-white/5 bg-[#121214] text-center">
          <Lock className="w-8 h-8 text-amber-400 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading text-2xl font-bold tracking-tight mb-2">Studio-only feature</div>
          <p className="text-sm text-zinc-400 max-w-md mx-auto mb-5">
            Team collaboration unlocks on the Studio plan. Invite up to 10 seats with role-based access.
          </p>
          <Button asChild className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="upgrade-to-studio">
            <Link to="/pricing">Upgrade to Studio →</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Invite form */}
          <div className="p-6 rounded-lg border border-white/5 bg-[#121214]">
            <div className="font-mono-tech text-[10px] text-zinc-500 mb-4 flex items-center gap-2">
              <Mail className="w-3 h-3" strokeWidth={1.75} /> INVITE · NEW · MEMBER
            </div>
            <form onSubmit={submitInvite} className="flex flex-col md:flex-row gap-3 items-end">
              <div className="flex-1 w-full">
                <Label className="font-mono-tech text-[10px] text-zinc-500">EMAIL</Label>
                <Input
                  type="email"
                  required
                  data-testid="invite-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
                />
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">ROLE</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="invite-role" className="mt-2 bg-[#0b0b0d] border-white/10 h-11 min-w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="send-invite"
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />}
                Send invite
              </Button>
            </form>
            <div className="text-[11px] text-zinc-500 mt-3">
              A link is created you can share by email or messaging app. The invitee must sign up with the same email.
            </div>
          </div>

          {/* Members list */}
          <div className="p-6 rounded-lg border border-white/5 bg-[#121214]" data-testid="team-members">
            <div className="font-mono-tech text-[10px] text-zinc-500 mb-4 flex items-center gap-2">
              <Users className="w-3 h-3" strokeWidth={1.75} />
              WORKSPACE · MEMBERS · ({activeWs?.name || "—"})
            </div>
            {members.length === 0 ? (
              <div className="text-sm text-zinc-500 text-center py-6">No members yet — invite your first teammate above.</div>
            ) : (
              <div className="space-y-2">
                {[...active, ...pending].map((m) => (
                  <div
                    key={m.id}
                    data-testid={`member-${m.id}`}
                    className="flex items-center gap-3 p-3 rounded bg-[#0b0b0d] border border-white/5"
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center font-heading font-bold">
                      {m.email.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{m.email}</div>
                      <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                        <span className="uppercase font-mono-tech">{m.role}</span>
                        {m.status === "pending" ? (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px] font-mono-tech uppercase">
                            <Clock className="w-2.5 h-2.5 mr-1" strokeWidth={2} /> Pending
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px] font-mono-tech uppercase">
                            <Check className="w-2.5 h-2.5 mr-1" strokeWidth={2} /> Active
                          </Badge>
                        )}
                      </div>
                    </div>
                    {m.status === "pending" && (
                      <button
                        onClick={() => copyInviteUrl(m.invite_token)}
                        data-testid={`copy-invite-${m.id}`}
                        title="Copy invite link"
                        className="text-zinc-500 hover:text-amber-400 p-2"
                      >
                        <Copy className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(m.id)}
                      data-testid={`remove-${m.id}`}
                      title="Remove"
                      className="text-zinc-500 hover:text-red-400 p-2"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seat usage hint */}
          <div className="text-[11px] text-zinc-500 font-mono-tech">
            {active.length + pending.length + 1} / {STUDIO_PLUS.includes(user?.tier) ? 10 : 999} seats used
            {" · "}
            <Link to="/pricing" className="text-amber-400 hover:text-amber-300">upgrade for more →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
